import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for loading images with progress tracking
 * @param {string} imageUrl - The URL of the image to load
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoLoad - Whether to start loading immediately (default: true)
 * @param {Function} options.onComplete - Callback when image is fully loaded
 * @param {Function} options.onError - Callback when loading fails
 * @returns {Object} - Loading state and controls
 */
export function useImageLoader(imageUrl, options = {}) {
  const { autoLoad = true, onComplete, onError } = options;
  
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);

  // Cleanup blob URL
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const loadImage = useCallback(async (url) => {
    if (!url) return;
    
    // Cleanup previous blob URL
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    
    setIsLoading(true);
    setIsLoaded(false);
    setError(null);
    setProgress(0);
    
    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status}`);
      }
      
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      
      if (total === 0) {
        // Fallback if no content-length header
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setProgress(100);
        setIsLoaded(true);
        onComplete?.(objectUrl);
      } else {
        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          chunks.push(value);
          receivedLength += value.length;
          
          const currentProgress = Math.round((receivedLength / total) * 100);
          setProgress(currentProgress);
        }
        
        const blob = new Blob(chunks);
        const objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setIsLoaded(true);
        onComplete?.(objectUrl);
      }
    } catch (err) {
      console.error('Error loading image:', err);
      setError(err.message);
      onError?.(err);
    } finally {
      setIsLoading(false);
    }
  }, [blobUrl, onComplete, onError]);

  // Auto-load when URL changes
  useEffect(() => {
    if (autoLoad && imageUrl) {
      loadImage(imageUrl);
    }
  }, [imageUrl, autoLoad, loadImage]);

  const reset = useCallback(() => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
    }
    setBlobUrl(null);
    setProgress(0);
    setIsLoading(false);
    setIsLoaded(false);
    setError(null);
  }, [blobUrl]);

  return {
    progress,
    isLoading,
    isLoaded,
    error,
    blobUrl,
    loadImage,
    reset
  };
}

/**
 * Preload an image in the background without tracking progress
 * @param {string} url - The URL to preload
 * @returns {Promise<string>} - Resolves with blob URL when loaded
 */
export function preloadImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No URL provided'));
      return;
    }
    
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = () => reject(new Error(`Failed to preload: ${url}`));
    img.src = url;
  });
}

/**
 * Preload multiple images in parallel
 * @param {string[]} urls - Array of URLs to preload
 * @returns {Promise<string[]>} - Resolves when all images are loaded
 */
export function preloadImages(urls) {
  return Promise.allSettled(urls.filter(Boolean).map(preloadImage));
}

export default useImageLoader;
